// =====================================================================
// Doc-type → validity duration (in days)
// =====================================================================
//
// Source: the business list of reports/documents that carry an auto
// expiration window measured from their EFFECTIVE date (the date the
// report itself states — NOT the upload date). Keyed by the canonical
// `docType` strings in `fileDocGroupAndTypesFallback` so a lookup matches
// what the user actually picks in the doc-type dropdown.
//
// When a file in the upload queue is assigned one of these doc types, the
// queue auto-fills `reportGuidelineExpirationDays` with the mapped value and
// the expiration date is computed as `reportEffectiveDate + days`. The
// effective date is still entered manually (these reports are almost never
// effective on the upload date), so the queue highlights mapped files that
// are missing an effective date rather than defaulting it to today.
//
// Business list → canonical taxonomy doc type (Assets / CPL / Title / COGS
// windows updated 2026-09-23):
//   Background Report(s) 90d        → "Background Report"
//   OFAC 30d                        → "Borrower OFAC Report" / "Entity OFAC Report"
//   Credit Report(s) 90d            → "Credit Report"
//   Property Profile 90d            → "Subject Property Record"
//   Flood Certificate 90d           → "Flood Certificate"
//   Assets (Liquidity) 45d          → "Bank Statement" / "Brokerage Account Statement"
//   Appraisal 120d                  → "Appraisal PDF"
//   Internal Property Valuation 120d→ "Secondary Valuation - Internal"
//   CDA 120d                        → "Secondary Valuation - CDA/ARR"
//   CPL 90d                         → "Closing Protection Letter" / "Title - CPL"
//   Prelim/Title Commitment 90d     → "Preliminary Title Policy"
//   Payoff 30d                      → "Payoff Letter"
//   VOM 30d                         → "Verification of Mortgage"
//   Feasibility/Permit 120d         → "Project Feasibility Analysis" / "Permits"
//   COGS 60d                        → "COGS"
//   FundingShield Certificate 30d   → "Funding Shield Verification" / "Funding Shield Request"
//
// NOT mapped (no matching doc type exists in the taxonomy yet): "Google
// Search" (30d) and "HOA Questionnaire" (60d). Add a doc type for them in
// `fileDocGroupAndTypesFallback` first, then add the entry here.
export const DOC_TYPE_EXPIRATION_DAYS = {
  // Borrower Reports
  "Background Report": 90,
  "Borrower OFAC Report": 30,
  "Entity OFAC Report": 30,
  "Credit Report": 90,
  // Property Documents
  "Subject Property Record": 90,
  "Flood Certificate": 90,
  "Payoff Letter": 30,
  "Verification of Mortgage": 30,
  // Liquidity
  "Bank Statement": 45,
  "Brokerage Account Statement": 45,
  // Appraisal / valuations
  "Appraisal PDF": 120,
  "Secondary Valuation - Internal": 120,
  "Secondary Valuation - CDA/ARR": 120,
  // Title / Closing
  "Closing Protection Letter": 90,
  "Title - CPL": 90,
  "Preliminary Title Policy": 90,
  "Funding Shield Verification": 30,
  "Funding Shield Request": 30,
  // Third Party Reports
  "Project Feasibility Analysis": 120,
  Permits: 120,
  // Entity Docs
  COGS: 60,
  // General Contractor Docs — SOFT defaults (see the note below). Contractor
  // licences and insurance policies are annual far more often than not, so 365
  // from the effective date is the right seed; the real date is printed on the
  // document, and the GC record carries it in `licenseExpirationDate` /
  // `insuranceExpirationDate`, which is what the Contacts card ages against.
  "Contractor License": 365,
  "Contractor General Liability Insurance": 365,
  "Contractor Workman's Compensation Insurance": 365,
};

// Doc types whose duration is a "soft" default rather than a hard rule —
// the value above seeds the field, but the user can override it per file.
// These are the cases flagged in the thread where the real window varies
// (e.g. bank/brokerage statements, where two months may be required and the
// effective date is read off the statement itself). Hard types (everything
// else in the map) auto-fill a fixed, read-only number.
export const SOFT_DURATION_DOC_TYPES = new Set([
  "Bank Statement",
  "Brokerage Account Statement",
  // A contractor credential expires on the date PRINTED on it, not N days
  // after it was issued — a two-year licence would otherwise age out in one.
  // 365 is the common case and only a seed; the operator overrides per file,
  // and the authority stays the GC record's own expiration dates.
  "Contractor License",
  "Contractor General Liability Insurance",
  "Contractor Workman's Compensation Insurance",
]);

/**
 * Return the auto validity duration (in days) for a doc type, or null when
 * the type carries no defined expiration window.
 * @param {string} docType
 * @returns {number|null}
 */
export const getExpirationDaysForDocType = (docType) => {
  if (!docType) return null;
  const days = DOC_TYPE_EXPIRATION_DAYS[docType];
  return typeof days === "number" ? days : null;
};

/**
 * A "hard" duration doc type carries a fixed, non-editable window: it's in
 * the map AND not flagged soft. Soft types (and unmapped types) keep an
 * editable duration so the user can enter/adjust the number of days.
 * @param {string} docType
 * @returns {boolean}
 */
export const isHardDurationDocType = (docType) =>
  getExpirationDaysForDocType(docType) != null &&
  !SOFT_DURATION_DOC_TYPES.has(docType);

/**
 * The validity window (days) a stored document is actually judged by.
 *
 * A HARD type always uses the current rule above, even when the doc carries an
 * older number in `reportGuidelineExpirationDays` (stamped at upload) — so
 * changing a window here re-ages every existing doc of that type instead of
 * only new uploads, and the Duration pill (which shows the rule) never
 * disagrees with the Expires date. Soft / unmapped types keep the stored,
 * user-entered number and fall back to the rule.
 * @param {object} doc  a loan document
 * @returns {number|null}
 */
export const resolveDocExpirationDays = (doc) => {
  const docType = doc?.docType;
  if (isHardDurationDocType(docType)) return getExpirationDaysForDocType(docType);
  return doc?.reportGuidelineExpirationDays ?? getExpirationDaysForDocType(docType);
};

// Doc types that carry an EXPLICIT expiration date printed on the document
// itself (e.g. a driver's license / passport), rather than a validity window
// measured from an effective date. For these the user picks the expiration
// date directly; we back into the stored model by setting the effective date
// to today and the duration to (expiration − today). Effective Start Date and
// Duration are not user-meaningful for these and render as "N/A".
export const EXPLICIT_EXPIRATION_DOC_TYPES = new Set([
  "Photo Identification",
  "Passport",
]);

export const isExplicitExpirationDocType = (docType) =>
  !!docType && EXPLICIT_EXPIRATION_DOC_TYPES.has(docType);

// Today as a timezone-stable YYYY-MM-DD (local calendar date).
export const getTodayYmd = () => {
  const now = new Date();
  const yy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

/**
 * For an explicit-expiration doc type: given the expiration date the user
 * picked, derive the stored fields — effective date = today, duration =
 * (expiration − today) in days. A blank pick clears both.
 * @param {string} expirationYmd  YYYY-MM-DD
 * @returns {{reportEffectiveDate: string|null, reportGuidelineExpirationDays: number|null}}
 */
export const deriveFromExpirationDate = (expirationYmd) => {
  if (!expirationYmd) {
    return { reportEffectiveDate: null, reportGuidelineExpirationDays: null };
  }
  return {
    reportEffectiveDate: getTodayYmd(),
    reportGuidelineExpirationDays: daysUntilDate(expirationYmd),
  };
};

// "Dual date" doc types (bank/brokerage statements) expose BOTH an effective
// start date AND an expiration date as independent pickers; Duration is N/A.
// The stored model still uses effective + duration, so we store the picked
// start as the effective date and the duration as (expiration − start) — NOT
// (expiration − today), so the chosen expiration round-trips exactly instead
// of drifting day to day.
export const DUAL_DATE_DOC_TYPES = new Set([
  "Bank Statement",
  "Brokerage Account Statement",
]);

export const isDualDateDocType = (docType) =>
  !!docType && DUAL_DATE_DOC_TYPES.has(docType);

// Whole calendar days between two date-only values (toYmd − fromYmd), compared
// on local fields so the count is timezone-stable. Returns null if either is
// missing/invalid.
export const daysBetween = (fromYmd, toYmd) => {
  const parse = (ymd) => {
    const datePart = String(ymd || "").slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return null;
    const [y, m, d] = datePart.split("-").map(Number);
    return new Date(y, m - 1, d);
  };
  const f = parse(fromYmd);
  const t = parse(toYmd);
  if (!f || !t) return null;
  return Math.round((t.getTime() - f.getTime()) / 86400000);
};

/**
 * For a dual-date doc type: given the picked start + expiration dates, derive
 * the stored fields — effective = start (or today when only an expiration is
 * set), duration = (expiration − effective). Either being blank clears the
 * derived duration.
 * @param {string} startYmd
 * @param {string} expirationYmd
 * @returns {{reportEffectiveDate: string|null, reportGuidelineExpirationDays: number|null}}
 */
export const deriveDualDateFields = (startYmd, expirationYmd) => {
  const effective = startYmd || (expirationYmd ? getTodayYmd() : null);
  const days =
    effective && expirationYmd ? daysBetween(effective, expirationYmd) : null;
  return { reportEffectiveDate: effective, reportGuidelineExpirationDays: days };
};

/**
 * Compute the expiration (end) date as `effectiveDate + days`, returned as a
 * timezone-stable `YYYY-MM-DD` string. Date math is done on the calendar
 * fields (not on a UTC instant) so the result never drifts a day across
 * timezones — the same guarantee `formatDate` relies on.
 *
 * @param {string|Date} effectiveDate  date-only value (YYYY-MM-DD or ISO)
 * @param {number} days
 * @returns {string|null}  YYYY-MM-DD, or null when inputs are missing/invalid
 */
export const computeExpirationDate = (effectiveDate, days) => {
  if (!effectiveDate || !days || typeof days !== "number") return null;
  const s =
    effectiveDate instanceof Date
      ? effectiveDate.toISOString()
      : String(effectiveDate);
  const datePart = s.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return null;
  const [y, m, d] = datePart.split("-").map(Number);
  // Local Date with day-of-month offset by `days` — JS normalizes overflow
  // (e.g. day 61) across months/years. Reading back the local fields keeps
  // the calendar date intact regardless of the runtime timezone.
  const end = new Date(y, m - 1, d + days);
  if (Number.isNaN(end.getTime())) return null;
  const yy = end.getFullYear();
  const mm = String(end.getMonth() + 1).padStart(2, "0");
  const dd = String(end.getDate()).padStart(2, "0");
  return `${yy}-${mm}-${dd}`;
};

/**
 * Whole calendar days from the reference day until the given date. Positive when
 * the date is in the future, 0 same day, negative once it's passed. Compared on
 * local calendar fields (both floored to midnight) so the count never drifts a
 * day across timezones or by time-of-day.
 *
 * `nowMs` defaults to today, but callers pass the loan's closing date for a
 * CLOSED loan so its docs are judged as of closing rather than today
 * (see utils/documents/validityReference).
 *
 * @param {string|Date} date  date-only value (YYYY-MM-DD or ISO)
 * @param {number} [nowMs]    epoch ms to measure from (defaults to now)
 * @returns {number|null}
 */
export const daysUntilDate = (date, nowMs = Date.now()) => {
  if (!date) return null;
  const s = date instanceof Date ? date.toISOString() : String(date);
  const datePart = s.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return null;
  const [y, m, d] = datePart.split("-").map(Number);
  const target = new Date(y, m - 1, d);
  const now = new Date(nowMs);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - today.getTime()) / 86400000);
};
